import { useEffect, useMemo } from "react";
import { useWebSocket } from "../contexts/WebSocketContext";

const RealTimeNotifications = () => {
  const { notifications, clearNotification, clearAllNotifications } =
    useWebSocket();

  // Remove duplicate notifications based on message and type
  const uniqueNotifications = useMemo(() => {
    const seen = new Set();
    return notifications.filter((notification) => {
      const key = `${notification.type}-${notification.message}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [notifications]);

  // Auto-close notifications after 5 seconds
  useEffect(() => {
    uniqueNotifications.forEach((notification) => {
      if (notification.autoClose !== false) {
        setTimeout(() => {
          clearNotification(notification.id);
        }, 5000);
      }
    });
  }, [uniqueNotifications, clearNotification]);

  if (uniqueNotifications.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2">
      {uniqueNotifications.slice(-3).map((notification) => (
        <div
          key={notification.id}
          className="bg-card-bg dark:bg-gray-800 border border-card-border dark:border-gray-700 rounded-xl shadow-lg dark:shadow-2xl dark:shadow-black/20 p-4 max-w-sm animate-slide-in-right backdrop-blur-sm"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                {notification.type === "child_event_created" && (
                  <span className="text-blue-500 dark:text-blue-400">👶</span>
                )}
                {notification.type === "milestone_completed" && (
                  <span className="text-green-500 dark:text-green-400">🎉</span>
                )}
                {notification.type === "health_alert" && (
                  <span className="text-red-500 dark:text-red-400">⚠️</span>
                )}
                {notification.type === "user_status" && (
                  <span className="text-gray-500 dark:text-gray-400">👤</span>
                )}
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {notification.title || "New Update"}
                </h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {notification.message || notification.description}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {notification.timestamp?.toLocaleTimeString()}
              </p>
            </div>
            <button
              onClick={() => clearNotification(notification.id)}
              className="ml-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      ))}

      {uniqueNotifications.length > 3 && (
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-2 text-center backdrop-blur-sm">
          <button
            onClick={() => {}}
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
          >
            +{uniqueNotifications.length - 3} more notifications
          </button>
        </div>
      )}

      {uniqueNotifications.length > 1 && (
        <div className="text-center">
          <button
            onClick={clearAllNotifications}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline transition-colors"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default RealTimeNotifications;
